import React, { useState, useEffect } from 'react';
// material
import { Table, Button, TableRow, TableBody, TableCell, TextField, Container, TableContainer, Modal, Box, Grid } from '@mui/material';
// components
import Page from 'src/components/Page';
import Scrollbar from 'src/components/Scrollbar';
import { UserListHead } from 'src/sections/@dashboard/user';
import axios from 'axios';
import { apiHeader } from 'src/redux/actions/ApiHeader';
import { baseUrlPostGres } from 'src/redux/constant';
import { toast } from 'react-toastify';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import { Delete, Edit } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { LogoutAction } from 'src/redux/actions/AuthAction';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
    { id: 'c_no', label: '#', alignRight: false },
    { id: 'c_name', label: 'Name', alignRight: false },
    { id: 'c_email', label: 'Email', alignRight: false },
    { id: 'c_action', label: 'Action', alignRight: false },
];

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
};

export default function Users() {
    const dispatch = useDispatch();
    const [userList, setUserList] = useState([]);
    const [formData, setFormData] = useState({});
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [error, setError] = useState("");

    const handleAddModalOpen = () => setAddModalOpen(true);
    const handleAddModalClose = () => {
        setAddModalOpen(false);
        setFormData({});
        setError("");
    };

    const handleEditModalOpen = (user) => {
        setFormData(user);
        setEditModalOpen(true);
    };
    const handleEditModalClose = () => {
        setEditModalOpen(false);
        setFormData({});
        setError("");
    };

    const fetchUserList = () => {
        axios
            .get(`${baseUrlPostGres()}/api/users/list`, { headers: apiHeader() })
            .then(response => {
                if (response.data.message === "Unauthorized please login again!") {
                    dispatch(LogoutAction(response.data.message));
                }
                setUserList(response.data.success);
            })
            .catch(errors => {
                console.error("user list error", errors);
                toast.error("Failed to fetch users.");
            });
    };

    useEffect(() => {
        fetchUserList();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleAddUser = async () => {
        if (!formData.name || !formData.email || !formData.password) {
            setError("All fields are required.");
            return;
        }
        setError("");
        await axios
            .post(`${baseUrlPostGres()}/api/users/add`, formData, { headers: apiHeader() })
            .then(response => {
                if (response.data.message === "Unauthorized please login again!") {
                    dispatch(LogoutAction(response.data.message));
                }
                fetchUserList();
                toast.success("User added successfully");
                handleAddModalClose();
            })
            .catch(errors => {
                console.error("add user error", errors);
                toast.error(errors.response?.data?.error || "Failed to add user.");
            });
    };

    const handleUpdateUser = async () => {
        if (!formData.name || !formData.email) {
            setError("Name and Email are required.");
            return;
        }
        setError("");

        // Prepare data, don't send empty password
        const dataToUpdate = {
            name: formData.name,
            email: formData.email
        };
        if (formData.password && formData.password.trim() !== "") {
            dataToUpdate.password = formData.password;
        }

        await axios
            .put(`${baseUrlPostGres()}/api/users/${formData.id}`, dataToUpdate, { headers: apiHeader() })
            .then(response => {
                if (response.data.message === "Unauthorized please login again!") {
                    dispatch(LogoutAction(response.data.message));
                }
                fetchUserList();
                toast.success("User updated successfully");
                handleEditModalClose();
            })
            .catch(errors => {
                console.error("update user error", errors);
                toast.error(errors.response?.data?.error || "Failed to update user.");
            });
    };

    const handleDeleteUser = async (user) => {
        if (window.confirm(`Are you sure you want to delete ${user.name}?`)) {
            await axios.delete(`${baseUrlPostGres()}/api/users/${user.id}`, { headers: apiHeader() })
                .then(response => {
                    if (response.data.message === "Unauthorized please login again!") {
                        dispatch(LogoutAction(response.data.message));
                    }
                    fetchUserList();
                    toast.success("User deleted successfully");
                })
                .catch(errors => {
                    console.error("delete user error", errors);
                    toast.error("Failed to delete user.");
                });
        }
    };

    return (
        <>
            <Page title="Users">
                <Container maxWidth="lg">
                    <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                        <HeaderBreadcrumbs
                            heading='User Management'
                            links={[{ name: 'Dashboard', href: "/dashboard" }, { name: 'Users' }]}
                        />
                        <Button variant="contained" onClick={handleAddModalOpen}>Add User</Button>
                    </Box>
                    <Scrollbar>
                        <TableContainer sx={{ minWidth: 800 }}>
                            <Table>
                                <UserListHead headLabel={TABLE_HEAD} />
                                <TableBody>
                                    {userList && userList.map((user, i) => (
                                        <TableRow key={user.id}>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>
                                                <Button variant="contained" color="success" sx={{ mr: 1 }} onClick={() => handleEditModalOpen(user)}><Edit /></Button>
                                                <Button variant="contained" color="error" onClick={() => handleDeleteUser(user)}><Delete /></Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Scrollbar>
                </Container>
            </Page>

            {/* Add User Modal */}
            <Modal open={addModalOpen} onClose={handleAddModalClose}>
                <Box sx={style}>
                    <h2>Add New User</h2>
                    <Grid container spacing={3}>
                        <Grid item xs={12}><TextField fullWidth label="Name" name="name" onChange={handleChange} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Email" name="email" type="email" onChange={handleChange} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Password" name="password" type="password" onChange={handleChange} /></Grid>
                        {error && <Grid item xs={12}><p style={{ color: 'red' }}>{error}</p></Grid>}
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleAddModalClose} sx={{ mr: 1 }}>Cancel</Button>
                        <Button variant="contained" onClick={handleAddUser}>Submit</Button>
                    </Box>
                </Box>
            </Modal>

            {/* Edit User Modal */}
            <Modal open={editModalOpen} onClose={handleEditModalClose}>
                <Box sx={style}>
                    <h2>Edit User</h2>
                    <Grid container spacing={3}>
                        <Grid item xs={12}><TextField fullWidth label="Name" name="name" value={formData.name || ''} onChange={handleChange} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="Email" name="email" type="email" value={formData.email || ''} onChange={handleChange} /></Grid>
                        <Grid item xs={12}><TextField fullWidth label="New Password" name="password" type="password" placeholder="Leave blank to keep current password" onChange={handleChange} /></Grid>
                        {error && <Grid item xs={12}><p style={{ color: 'red' }}>{error}</p></Grid>}
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleEditModalClose} sx={{ mr: 1 }}>Cancel</Button>
                        <Button variant="contained" onClick={handleUpdateUser}>Update</Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}
